import {
	CURSOR,
	drawRectangle,
	getTextColor,
	getTrimmedText,
	GROUP_SECTION_PADDING_LEFT,
	GROUP_SECTION_SPACING_LEVEL_FOUR,
	GROUP_SECTION_WIDTH,
	GROUP_TYPE,
	GROUP_SECTION_PADDING_LEFT_SMALL,
	GROUP_SECTION_TEXT_GREY,
	EXPAND_ICON_WIDTH,
	drawBorder,
	GROUP_SECTION_PROJECT_BORDER_WIDTH,
	GROUP_SECTION_ACTION_BUTTON_SIZE,
	GROUP_SECTION_EXPAND_ICON_WIDTH,
	GROUP_SECTION_EXPAND_ICON_HEIGHT,
} from '../../canvas-timeline/canvas_timeline_util';
import Group from '../../canvas-timeline/canvas_timeline_group';
import {cacheManager, COMMON_IMAGE} from '../../canvas-timeline/canvas_timeline_cache_manager';
import {interactionManager} from '../../canvas-timeline/canvas_timeline_interaction_manager';
import Util from '../../../../forecast-app/shared/util/util';
import {drawExpandedGroupHighlight} from '../../DrawingUtils';
import EventManager from '../../EventManager';
import {drawSidebarTaskSetChip, measureSidebarTaskSetChip} from '../items/taskSetSidebarChip';
import {taskSetColour} from '../../../../forecast-app/project-tab/projects/scoping-page/task-set/taskSetColours';
import {hasFeatureFlag} from '../../../../forecast-app/shared/util/FeatureUtil';
import {isProjectLocked} from '../../../../forecast-app/shared/util/projectStageUtil';

class TaskGroup extends Group {
	constructor(pageComponent, data) {
		super(pageComponent, GROUP_TYPE.TASK, data);

		if (data.subTaskLevel === undefined) {
			data.subTaskLevel = 0;
		}
	}

	addSubTaskLevel(subTaskLevel) {
		this.data.subTaskLevel = subTaskLevel;
		if (this.data.groups) {
			this.data.groups.forEach(group => {
				group.addSubTaskLevel(subTaskLevel + 1);
			});
		}
	}

	refreshData(updatedTask) {
		this.data.name = updatedTask.name;
	}

	onItemCreate(startDate, endDate) {
		const {sharedContext} = this.pageComponent.props;
		const {project} = this.data;

		if (!sharedContext && !isProjectLocked(project)) {
			EventManager.onItemCreate(this.pageComponent, this, startDate, endDate);
		}
	}

	onClick() {
		const {sharedContext, showTaskModal} = this.pageComponent.props;

		if (!sharedContext) {
			const {task, disabled} = this.data;
			if (!disabled) {
				showTaskModal(task.companyTaskId);
			}
		}
	}

	draw(canvasContext, x, y) {
		const width = GROUP_SECTION_WIDTH;
		const {data, height, groupType, expanded} = this;
		super.draw(x, y, width, height, this);
		const {name, id, color, isInProjectGroup, isProjectTimeline} = data;
		const cacheId = `${groupType}-${id}`;
		interactionManager.addCursorStyleArea(x, y, width, height, CURSOR.POINTER, true);
		const taskDone = data.task.done;
		const connectedProjectOffset = isInProjectGroup ? GROUP_SECTION_PADDING_LEFT : 0;

		const cachedData = cacheManager.get(groupType, cacheId);
		if (cachedData) {
			canvasContext.drawImage(cachedData, x, y, width, this.itemRowHeight);
			return;
		}

		const fontSize = 11;
		const textColor = getTextColor();

		const drawGroup = () => {
			const subTaskLevelOffset = data.subTaskLevel * GROUP_SECTION_PADDING_LEFT;
			const topBottomBorderThickness = 1;
			const smallestTaskOffsetX =
				GROUP_SECTION_SPACING_LEVEL_FOUR +
				GROUP_SECTION_PADDING_LEFT * 2 +
				GROUP_SECTION_PADDING_LEFT_SMALL +
				EXPAND_ICON_WIDTH;
			// PIKA-748 F4: the set marker on the SIDEBAR row.
			//
			// The bar chip keeps the position and the click; this is identity only, in
			// the set's colour. Clare's functional review asked for the sidebar because
			// a bar's width varies with duration and zoom, so the bar marker degrades to
			// an icon and then to a 4px edge exactly when a run of identically-named
			// instances is hardest to read. This row is a fixed width and always drawn.
			//
			// It also covers the one case the bar never can: under Weekend Settings =
			// exclude, an instance dated on a weekend draws in the wrong week or not at
			// all, but its sidebar row is still here.
			// Measured before the name, so the name is trimmed around the marker rather
			// than drawn under it. The decision and the width live in
			// taskSetSidebarChip, which is unit-tested; this file cannot be, because
			// drawing into the group section pulls in most of the timeline.
			//
			// Guarded on fullAccessToProject, exactly as the BAR chip is
			// (task_item.js) and as TaskType.taskSet/taskSetSequence are on the server:
			// the rule describes work across the whole project, so it is not for
			// someone with partial access to it. A program or connected-group timeline
			// is one surface carrying several projects, so without this the sidebar
			// would mark an instance the bar deliberately leaves plain.
			const taskSetChip = measureSidebarTaskSetChip({
				canvasContext,
				task: data.task,
				pageComponent: this.pageComponent,
				flagEnabled: hasFeatureFlag('recurring_tasks_set'),
				gap: GROUP_SECTION_PADDING_LEFT,
			});

			const maxNameWidth =
				GROUP_SECTION_WIDTH -
				connectedProjectOffset -
				smallestTaskOffsetX -
				subTaskLevelOffset -
				GROUP_SECTION_PADDING_LEFT -
				GROUP_SECTION_ACTION_BUTTON_SIZE -
				taskSetChip.reservedWidth;

			if (!isProjectTimeline) {
				drawExpandedGroupHighlight(canvasContext, x, y, height, width);
			}

			// right project colored border
			drawBorder(
				canvasContext,
				x + width - GROUP_SECTION_PROJECT_BORDER_WIDTH,
				y - 2 - topBottomBorderThickness * 2,
				height + topBottomBorderThickness * 2,
				true,
				color,
				GROUP_SECTION_PROJECT_BORDER_WIDTH
			);

			if (data.expandable) {
				canvasContext.drawImage(
					cacheManager.getCommonImage(
						expanded ? COMMON_IMAGE.EXPAND_GROUP_ICON : COMMON_IMAGE.EXPAND_GROUP_CLOSED_ICON
					),
					x +
						connectedProjectOffset +
						GROUP_SECTION_SPACING_LEVEL_FOUR +
						subTaskLevelOffset -
						GROUP_SECTION_EXPAND_ICON_WIDTH / 2,
					y + height / 2 - GROUP_SECTION_EXPAND_ICON_HEIGHT / 2,
					GROUP_SECTION_EXPAND_ICON_WIDTH,
					GROUP_SECTION_EXPAND_ICON_HEIGHT
				);
			}

			// task icon
			const iconHeight = 16;
			const iconWidth = 16;
			const taskIcon =
				data.subTaskLevel > 0
					? taskDone
						? COMMON_IMAGE.TASK_DONE_BLACK
						: data.isStarted
						? COMMON_IMAGE.SUB_TASK_BLACK
						: COMMON_IMAGE.SUB_TASK_NOT_STARTED
					: taskDone
					? COMMON_IMAGE.TASK_DONE_BLACK
					: data.isStarted
					? COMMON_IMAGE.TASK_BLACK
					: COMMON_IMAGE.TASK_NOT_STARTED;
			canvasContext.drawImage(
				cacheManager.getCommonImage(taskIcon),
				x +
					connectedProjectOffset +
					GROUP_SECTION_SPACING_LEVEL_FOUR +
					GROUP_SECTION_PADDING_LEFT +
					GROUP_SECTION_EXPAND_ICON_WIDTH / 2 +
					subTaskLevelOffset,
				y + height / 2 - iconHeight / 2,
				iconWidth,
				iconHeight
			);

			// task name
			canvasContext.fillStyle = data.isStarted ? textColor : GROUP_SECTION_TEXT_GREY;
			canvasContext.font = `600 ${fontSize}px ` + Util.getFontFamily();
			const nameTrimmed = getTrimmedText(canvasContext, name, maxNameWidth);
			const nameX =
				x +
				connectedProjectOffset +
				GROUP_SECTION_SPACING_LEVEL_FOUR +
				GROUP_SECTION_PADDING_LEFT * 2 +
				GROUP_SECTION_PADDING_LEFT_SMALL +
				EXPAND_ICON_WIDTH +
				subTaskLevelOffset;
			canvasContext.fillText(nameTrimmed, nameX, y + height / 2 + fontSize / 3);
			// Measured once, under the name's own font, and reused by the marker and the
			// strikethrough below -- measureText is the costly call on this path and it
			// runs per row per frame. Skipped on a row that needs neither, so an ordinary
			// open task costs exactly what it did before the marker existed: the
			// strikethrough was the only caller, and it was already behind `taskDone`.
			const nameWidth = taskSetChip.show || taskDone ? canvasContext.measureText(nameTrimmed).width : 0;

			// PIKA-748 F4: a no-op when the row carries no marker, so the placement and
			// the branch both live in taskSetSidebarChip where they are unit-tested.
			drawSidebarTaskSetChip({
				canvasContext,
				chip: taskSetChip,
				taskSetId: data.task?.taskSetId,
				nameX,
				nameWidth,
				gap: GROUP_SECTION_PADDING_LEFT,
				y,
				rowHeight: height,
				colourFor: taskSetColour,
			});

			// task done name strikethrough
			if (taskDone) {
				const strikethroughPadding = 4;
				drawRectangle(
					canvasContext,
					nameX - strikethroughPadding / 2,
					y + height / 2,
					nameWidth + strikethroughPadding,
					0.75,
					{
						backgroundColor: textColor,
					}
				);
			}
		};
		drawGroup();
	}
}

export default TaskGroup;
